import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import { Checkbox } from "../../../components/ui/Checkbox";


import { listServices } from "../../../lib/services";
import { listStaff } from "../../../lib/staff";

const AppointmentBookingModal = ({ client, isOpen, onClose, onBook }) => {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);

  const [serviceIds, setServiceIds] = useState([]); // multi
  const [staffId, setStaffId] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    let alive = true;
    setIsFetching(true);

    Promise.all([listServices({ include_inactive: false }), listStaff()])
      .then(([svc, st]) => {
        if (!alive) return;
        setServices(Array.isArray(svc) ? svc : []);
        setStaff(Array.isArray(st) ? st : []);
      })
      .catch((e) => {
        console.error("Nu am putut încărca servicii/staff:", e?.response?.data || e);
        if (!alive) return;
        setServices([]);
        setStaff([]);
      })
      .finally(() => {
        if (!alive) return;
        setIsFetching(false);
      });

    return () => {
      alive = false;
    };
  }, [isOpen]);

  const staffOptions = useMemo(() => {
    return [
      { value: "", label: t("clients.booking.selectSpecialist") },
      ...(staff || []).map((m) => ({
        value: String(m.id),
        label:
          m.full_name ||
          m.name ||
          [m.first_name, m.last_name].filter(Boolean).join(" ") ||
          `User #${m.id}`,
      })),
    ];
  }, [staff]);

  const timeSlots = useMemo(() => {
    // simplu, la jumătate de oră (poți adapta pe slotMinutes)
    const out = [];
    for (let h = 8; h <= 20; h++) {
      out.push({ value: `${String(h).padStart(2, "0")}:00`, label: `${String(h).padStart(2, "0")}:00` });
      out.push({ value: `${String(h).padStart(2, "0")}:30`, label: `${String(h).padStart(2, "0")}:30` });
    }
    return out;
  }, []);

  const selectedServices = useMemo(() => {
    const map = new Map((services || []).map((s) => [Number(s.id), s]));
    return serviceIds.map((id) => map.get(Number(id))).filter(Boolean);
  }, [serviceIds, services]);

  const totals = useMemo(() => {
    const totalMinutes = selectedServices.reduce(
      (sum, s) => sum + (Number(s.duration_minutes) || 30),
      0
    );
    const totalPrice = selectedServices.reduce(
      (sum, s) => sum + (Number(s.price) || 0),
      0
    );
    return { totalMinutes, totalPrice };
  }, [selectedServices]);

  const toggleService = (id, checked) => {
    const sid = Number(id);
    if (!Number.isFinite(sid)) return;

    setServiceIds((prev) => {
      const set = new Set(prev.map(Number));
      if (checked) set.add(sid);
      else set.delete(sid);
      return Array.from(set);
    });
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  const resetForm = () => {
    setServiceIds([]);
    setStaffId("");
    setDate("");
    setTime("");
    setNotes("");
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!serviceIds.length) {
      alert(t("clients.booking.selectServiceAlert"));
      return;
    }
    if (!staffId) {
      alert(t("clients.booking.selectSpecialistAlert"));
      return;
    }
    if (!date || !time) {
      alert(t("clients.booking.selectDateTimeAlert"));
      return;
    }

    setIsLoading(true);
    try {
      await onBook({
        clientId: client?.id,
        serviceIds: serviceIds,
        staffId: Number(staffId),
        date,
        time,
        notes,
        // duration e optional; backend calculează automat din servicii
        duration: totals.totalMinutes || undefined,
      });

      resetForm();
      onClose();
    } catch (error) {
      console.error("Error booking appointment:", error?.response?.data || error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-background border-b border-border p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-card-foreground">
                {t("clients.booking.title")}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {t("clients.booking.clientLabel")} {client?.name} • {client?.email || client?.phone || ""}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {isFetching ? (
            <div className="text-sm text-muted-foreground">
              {t("clients.booking.loadingServicesStaff")}
            </div>
          ) : null}

          {/* Servicii multiple */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-foreground">
                {t("clients.booking.servicesLabel")}
              </label>
              {!!serviceIds.length && (
                <div className="text-xs text-muted-foreground">
                  {t("clients.booking.totalLabel", { min: totals.totalMinutes, price: totals.totalPrice })}
                </div>
              )}
            </div>

            <div className="border border-border rounded-lg p-3 max-h-56 overflow-y-auto bg-background space-y-2">
              {(services || []).length ? (
                services.map((s) => {
                  const sid = Number(s.id);
                  const checked = serviceIds.includes(sid);

                  return (
                    <div key={sid} className="flex items-center justify-between gap-3">
                      <Checkbox
                        checked={checked}
                        onChange={(e) => toggleService(sid, e.target.checked)}
                        label={s.name || `Serviciu #${sid}`}
                      />
                      <div className="text-xs text-muted-foreground whitespace-nowrap">
                        {(Number(s.duration_minutes) || 30)} min • {(Number(s.price) || 0)} RON
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-sm text-muted-foreground">
                  {t("clients.booking.noServices")}
                </div>
              )}
            </div>
          </div>

          {/* Staff */}
          <Select
            label={t("clients.booking.specialist")}
            options={staffOptions}
            value={staffId}
            onChange={(value) => setStaffId(value)}
            required
          />

          {/* Data + Ora */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label={t("clients.booking.dateLabel")}
              type="date"
              value={date}
              onChange={(e) => setDate(e?.target?.value)}
              min={getTomorrowDate()}
              required
            />

            <Select
              label={t("clients.booking.timeLabel")}
              options={timeSlots}
              value={time}
              onChange={(value) => setTime(value)}
              required
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("clients.booking.notesLabel")}
            </label>
            <textarea
              className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
              rows={3}
              placeholder={t("clients.booking.notesPlaceholder")}
              value={notes}
              onChange={(e) => setNotes(e?.target?.value)}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isLoading}
              iconName="Calendar"
              iconPosition="left"
              disabled={isFetching}
            >
              {t("clients.booking.confirm")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentBookingModal;
